import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { 
  CheckCircle, XCircle, CreditCard, Mail, 
  MessageSquare, Shield, AlertCircle
} from 'lucide-react';

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/(api)?\/?$/, "")}/api`;

const IntegrationsPage = () => {
  const [testingSMS, setTestingSMS] = useState(false);
  const [smsTestPhone, setSmsTestPhone] = useState('');
  const [smsTestMessage, setSmsTestMessage] = useState('Test mesajı - TesisKirala.com');

  const integrations = [
    {
      id: 'paytr',
      name: 'PayTR',
      description: 'Ödeme sistemi',
      icon: CreditCard,
      status: 'active',
      config: {
        merchant_id: '350537',
        test_mode: 'Aktif'
      }
    },
    {
      id: 'netgsm',
      name: 'NetGSM',
      description: 'SMS servisi',
      icon: MessageSquare,
      status: 'active',
      config: {
        username: '8503094413',
        header: 'TESISKIRALA'
      }
    },
    {
      id: 'resend',
      name: 'Resend',
      description: 'Email servisi',
      icon: Mail,
      status: 'active',
      config: {
        mode: 'Live'
      }
    },
    {
      id: 'google-auth',
      name: 'Google Auth',
      description: 'Emergent managed',
      icon: Shield,
      status: 'active',
      config: {
        provider: 'Emergent'
      }
    }
  ];

  const handleTestSMS = async () => {
    if (!smsTestPhone || !smsTestMessage) {
      toast.error('Telefon ve mesaj alanlarını doldurun');
      return;
    }

    setTestingSMS(true);
    try {
      await axios.post(
        `${API}/admin/test-sms`,
        { phone: smsTestPhone, message: smsTestMessage },
        { withCredentials: true }
      );
      toast.success('Test SMS gönderildi');
    } catch (error) {
      console.error('SMS test error:', error);
      toast.error(error.response?.data?.detail || 'SMS gönderilemedi');
    } finally {
      setTestingSMS(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">Entegrasyonlar</h1>
        <p className="text-sm text-slate-600 mt-1">
          Sistemde kullanılan 3. parti servisler
        </p>
      </div>

      {/* Integration Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {integrations.map((integration) => {
          const Icon = integration.icon;
          return (
            <Card key={integration.id}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-100 rounded-lg flex items-center justify-center">
                      <Icon className="w-6 h-6 text-slate-700" />
                    </div>
                    <div>
                      <CardTitle className="text-lg">{integration.name}</CardTitle>
                      <p className="text-sm text-slate-600">{integration.description}</p>
                    </div>
                  </div>
                  <Badge className={integration.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}>
                    {integration.status === 'active' ? (
                      <>
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Aktif
                      </>
                    ) : (
                      <>
                        <XCircle className="w-3 h-3 mr-1" />
                        Pasif
                      </>
                    )}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  {Object.entries(integration.config).map(([key, value]) => (
                    <div key={key} className="flex justify-between py-1">
                      <span className="text-slate-600 capitalize">{key.replace('_', ' ')}:</span>
                      <span className="font-medium">{value}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* SMS Test Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="w-5 h-5" />
            SMS Test
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-blue-600 mt-0.5" />
            <div className="flex-1 text-sm text-blue-800">
              NetGSM entegrasyonunu test etmek için bir telefon numarasına test SMS'i gönderebilirsiniz.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label>Telefon Numarası</Label>
              <Input
                placeholder="5551234567"
                value={smsTestPhone}
                onChange={(e) => setSmsTestPhone(e.target.value)}
              />
            </div>
            <div>
              <Label>Mesaj</Label>
              <Input
                placeholder="Test mesajınız"
                value={smsTestMessage}
                onChange={(e) => setSmsTestMessage(e.target.value)}
              />
            </div>
          </div>

          <Button
            onClick={handleTestSMS}
            disabled={testingSMS}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            {testingSMS ? 'Gönderiliyor...' : 'Test SMS Gönder'}
          </Button>
        </CardContent>
      </Card>

      {/* Info Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <div>
                <p className="font-semibold">PayTR</p>
                <p className="text-xs">Ödeme sistemi aktif</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <div>
                <p className="font-semibold">NetGSM</p>
                <p className="text-xs">SMS servisi aktif</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="w-5 h-5" />
              <div>
                <p className="font-semibold">Resend</p>
                <p className="text-xs">Email servisi aktif</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default IntegrationsPage;
